import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { PROXY_API_PATH } from '../constants';

// ─── Default values ───────────────────────────────────────────────────────────
export const THEME_DEFAULTS = {
    // Brand / accent
    primary:            '#2563eb',   // blue-600  – buttons, active states, links
    accent:             '#10b981',   // emerald-500 – hover highlights, CTAs

    // Navbar  (scrolled / opaque state)
    navBgLight:         '#ffffff',
    navBgDark:          '#0f172a',   // slate-900
    navTextLight:       '#1e293b',   // slate-800
    navTextDark:        '#f1f5f9',   // slate-100

    // Footer
    footerBgLight:      '#ffffff',
    footerBgDark:       '#0f172a',
    footerTextLight:    '#64748b',   // slate-500
    footerTextDark:     '#94a3b8',   // slate-400
    footerHeadingLight: '#0f172a',
    footerHeadingDark:  '#f1f5f9',
    footerBorderLight:  '#f1f5f9',   // slate-100
    footerBorderDark:   '#1e293b',   // slate-800

    // Page background (the root wrapper)
    pageBgLight:        '#f5f0eb',   // warm light beige
    pageBgDark:         '#020617',   // slate-950

    // Card / panel backgrounds
    cardBgLight:        '#ffffff',
    cardBgDark:         '#0f172a',   // slate-900
    cardBorderLight:    '#e8e0d8',   // warm beige border
    cardBorderDark:     '#1e293b',   // slate-800

    // Sidebar (admin panel)
    sidebarBgLight:     '#ffffff',
    sidebarBgDark:      '#0f172a',

    // Buttons
    btnPrimaryBg:       '#2563eb',   // primary button fill
    btnPrimaryText:     '#ffffff',   // primary button label
    btnPrimaryHover:    '#1d4ed8',   // primary button hover fill
    btnSecondaryBg:     '#ffffff',   // secondary/outline button fill
    btnSecondaryText:   '#2563eb',   // secondary button label + border
    btnSecondaryHover:  '#eff6ff',   // secondary button hover fill
};
const THEME_STORAGE_KEY = 'hm_theme_custom';
const HEX_COLOR_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const THEME_KEYS = Object.keys(THEME_DEFAULTS);

const normalizeHexColor = (value, fallback) => {
    if (typeof value !== 'string') return fallback;
    const trimmed = value.trim();
    if (!HEX_COLOR_RE.test(trimmed)) return fallback;

    const clean = trimmed.slice(1);
    const full = clean.length === 3
        ? clean.split('').map(char => char + char).join('')
        : clean;

    return `#${full.toLowerCase()}`;
};

// ── Contrast self-healing ────────────────────────────────────────────────────
// Old theme blobs (and hand-edited ones) can carry DARK-mode colours in the
// LIGHT-mode slots — e.g. footerHeadingLight #f1f5f9 on footerBgLight #ffffff,
// which painted the whole light-theme footer invisible. Instead of migrating
// every tenant's data, sanity-check each foreground against its background at
// normalize time: if the pair is unreadable, swap the foreground for whichever
// of its light/dark defaults suits the background's brightness.
const _lum = (hex) => {
    const full = String(hex || '').replace('#', '');
    const h = full.length === 3 ? full.split('').map(c => c + c).join('') : full;
    const ch = (i) => {
        const v = parseInt(h.slice(i, i + 2), 16) / 255;
        return isNaN(v) ? 0 : (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
    };
    return 0.2126 * ch(0) + 0.7152 * ch(2) + 0.0722 * ch(4);
};
const _contrast = (a, b) => {
    const [hi, lo] = [_lum(a), _lum(b)].sort((x, y) => y - x);
    return (hi + 0.05) / (lo + 0.05);
};
// [foreground key, its background key, light-default twin, dark-default twin]
const CONTRAST_GUARDS = [
    ['footerHeadingLight', 'footerBgLight', 'footerHeadingLight', 'footerHeadingDark'],
    ['footerTextLight',    'footerBgLight', 'footerTextLight',    'footerTextDark'],
    ['footerHeadingDark',  'footerBgDark',  'footerHeadingLight', 'footerHeadingDark'],
    ['footerTextDark',     'footerBgDark',  'footerTextLight',    'footerTextDark'],
    ['navTextLight',       'navBgLight',    'navTextLight',       'navTextDark'],
    ['navTextDark',        'navBgDark',     'navTextLight',       'navTextDark'],
];
const MIN_READABLE_CONTRAST = 2; // below this a colour pair is effectively invisible

export const normalizeThemeCustom = (value = {}) => {
    const normalized = {};
    for (const key of THEME_KEYS) {
        normalized[key] = normalizeHexColor(value?.[key], THEME_DEFAULTS[key]);
    }
    for (const [fg, bg, lightTwin, darkTwin] of CONTRAST_GUARDS) {
        if (_contrast(normalized[fg], normalized[bg]) < MIN_READABLE_CONTRAST) {
            // Pick the default that reads on THIS background — a light bg gets
            // the light-slot default (dark text), a dark bg the dark-slot one.
            normalized[fg] = _lum(normalized[bg]) > 0.4 ? THEME_DEFAULTS[lightTwin] : THEME_DEFAULTS[darkTwin];
        }
    }
    return normalized;
};

const readStoredThemeCustom = () => {
    try {
        // Server-injected theme (window.__HM_BRAND__.theme) wins — it's always
        // fresh and arrives in the HTML before the bundle executes, so the
        // very first painted pixel uses the tenant's brand colours. Seed
        // localStorage with the server value so subsequent reads stay in sync.
        const fromServer = (typeof window !== 'undefined'
            && window.__HM_BRAND__
            && typeof window.__HM_BRAND__.theme === 'object')
            ? window.__HM_BRAND__.theme : null;
        if (fromServer) {
            try { localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(fromServer)); } catch { /* private mode */ }
            return normalizeThemeCustom(fromServer);
        }
        const saved = localStorage.getItem(THEME_STORAGE_KEY);
        return saved ? normalizeThemeCustom(JSON.parse(saved)) : normalizeThemeCustom();
    } catch {
        return normalizeThemeCustom();
    }
};

const writeStoredThemeCustom = (theme) => {
    try { localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(theme)); } catch {}
};

const persistThemeCustom = async (theme) => {
    const token = localStorage.getItem('hm_admin_token') || '';
    const res = await fetch(`${PROXY_API_PATH}/admin/site-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-admin-token': token },
        body: JSON.stringify({ key: THEME_STORAGE_KEY, value: theme }),
    });

    const json = await res.json().catch(() => ({}));
    if (!res.ok || json?.ok === false) {
        throw new Error(json?.error || 'Failed to save theme settings.');
    }
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
/** Convert a hex colour (#rrggbb or #rgb) to "r g b" channel string for CSS rgb() */
const hexToRgbChannels = (hex) => {
    const clean = hex.replace('#', '');
    const full = clean.length === 3
        ? clean.split('').map(c => c + c).join('')
        : clean;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    if (isNaN(r) || isNaN(g) || isNaN(b)) return '37 99 235'; // fallback blue-600
    return `${r} ${g} ${b}`;
};

// ─── Build the injected CSS string ───────────────────────────────────────────
const buildCss = (t) => `
/* ── HolidaysMaster custom theme ───────────────────────── */
:root {
    --hm-primary:          ${t.primary};
    --hm-primary-rgb:      ${hexToRgbChannels(t.primary)};
    --hm-accent:           ${t.accent};
    --hm-accent-rgb:       ${hexToRgbChannels(t.accent)};

    --hm-nav-bg:           ${t.navBgLight};
    --hm-nav-text:         ${t.navTextLight};

    --hm-footer-bg:        ${t.footerBgLight};
    --hm-footer-text:      ${t.footerTextLight};
    --hm-footer-heading:   ${t.footerHeadingLight};
    --hm-footer-border:    ${t.footerBorderLight};

    --hm-page-bg:          ${t.pageBgLight};

    --hm-card-bg:          ${t.cardBgLight};
    --hm-card-border:      ${t.cardBorderLight};

    --hm-sidebar-bg:       ${t.sidebarBgLight};

    --hm-btn-primary-bg:      ${t.btnPrimaryBg};
    --hm-btn-primary-text:    ${t.btnPrimaryText};
    --hm-btn-primary-hover:   ${t.btnPrimaryHover};
    --hm-btn-secondary-bg:    ${t.btnSecondaryBg};
    --hm-btn-secondary-text:  ${t.btnSecondaryText};
    --hm-btn-secondary-hover: ${t.btnSecondaryHover};

    /* keep index.css body var in sync */
    --background:          ${t.pageBgLight};
    --foreground:          ${t.navTextLight};
    --card-bg:             ${t.cardBgLight};
    --card-border:         ${t.cardBorderLight};
}
.dark {
    --hm-nav-bg:           ${t.navBgDark};
    --hm-nav-text:         ${t.navTextDark};

    --hm-footer-bg:        ${t.footerBgDark};
    --hm-footer-text:      ${t.footerTextDark};
    --hm-footer-heading:   ${t.footerHeadingDark};
    --hm-footer-border:    ${t.footerBorderDark};

    --hm-page-bg:          ${t.pageBgDark};

    --hm-card-bg:          ${t.cardBgDark};
    --hm-card-border:      ${t.cardBorderDark};

    --hm-sidebar-bg:       ${t.sidebarBgDark};

    --hm-btn-primary-bg:      ${t.btnPrimaryBg};
    --hm-btn-primary-text:    ${t.btnPrimaryText};
    --hm-btn-primary-hover:   ${t.btnPrimaryHover};
    --hm-btn-secondary-bg:    ${t.btnSecondaryBg};
    --hm-btn-secondary-text:  ${t.btnSecondaryText};
    --hm-btn-secondary-hover: ${t.btnSecondaryHover};

    /* keep index.css body var in sync */
    --background:          ${t.pageBgDark};
    --foreground:          ${t.navTextDark};
    --card-bg:             ${t.cardBgDark};
    --card-border:         ${t.cardBorderDark};
}

/* ── Global: page background ─────────────────────────── */
body {
    background-color: var(--background) !important;
    color: var(--foreground);
}

/* ── Primary colour overrides ─────────────────────────── */
.hm-btn-primary {
    background-color: var(--hm-btn-primary-bg) !important;
    color: var(--hm-btn-primary-text) !important;
}
.hm-btn-primary:hover {
    background-color: var(--hm-btn-primary-hover) !important;
    filter: none;
}
.hm-btn-secondary {
    background-color: var(--hm-btn-secondary-bg) !important;
    color: var(--hm-btn-secondary-text) !important;
    border-color: var(--hm-btn-secondary-text) !important;
}
.hm-btn-secondary:hover {
    background-color: var(--hm-btn-secondary-hover) !important;
}
.hm-accent-bg {
    background-color: var(--hm-accent) !important;
}
.hm-accent-text {
    color: var(--hm-accent) !important;
}

/* ── Card backgrounds ─────────────────────────────────── */
.hm-card {
    background-color: var(--hm-card-bg) !important;
    border-color: var(--hm-card-border) !important;
}
`;

// ─── Inject / update the <style> tag ─────────────────────────────────────────
const STYLE_ID = 'hm-theme-custom';
const injectCss = (css) => {
    let el = document.getElementById(STYLE_ID);
    if (!el) {
        el = document.createElement('style');
        el.id = STYLE_ID;
        document.head.appendChild(el);
    }
    el.textContent = css;
};

// ─── Context ──────────────────────────────────────────────────────────────────
const ThemeCustomContext = createContext();

export const useThemeCustom = () => {
    const ctx = useContext(ThemeCustomContext);
    if (!ctx) throw new Error('useThemeCustom must be used within ThemeCustomProvider');
    return ctx;
};

export const ThemeCustomProvider = ({ children }) => {
    const [custom, setCustom] = useState(() => {
        const initial = readStoredThemeCustom();
        injectCss(buildCss(initial));
        return initial;
    });

    // Keep CSS in sync whenever custom changes after mount
    useEffect(() => {
        injectCss(buildCss(custom));
        // Tint the phone's browser bar with this site's brand colour, not the
        // emerald fallback baked into index.html.
        const meta = document.querySelector('meta[name="theme-color"]');
        if (meta && /^#[0-9a-f]{3,8}$/i.test(custom.primary || '')) {
            meta.setAttribute('content', custom.primary);
        }
    }, [custom]);

    const saveCustom = useCallback(async (updates) => {
        const previous = custom;
        const next = normalizeThemeCustom({ ...custom, ...updates });

        setCustom(next);
        writeStoredThemeCustom(next);

        try {
            await persistThemeCustom(next);
            return next;
        } catch (error) {
            setCustom(previous);
            writeStoredThemeCustom(previous);
            throw error;
        }
    }, [custom]);

    const resetCustom = useCallback(async () => {
        const previous = custom;
        const next = normalizeThemeCustom();

        setCustom(next);
        writeStoredThemeCustom(next);

        try {
            await persistThemeCustom(next);
            return next;
        } catch (error) {
            setCustom(previous);
            writeStoredThemeCustom(previous);
            throw error;
        }
    }, [custom]);

    const updateCustom = saveCustom;

    // Sync across tabs
    useEffect(() => {
        const handler = (e) => {
            if (e.key === THEME_STORAGE_KEY) {
                try {
                    const v = e.newValue ? normalizeThemeCustom(JSON.parse(e.newValue)) : normalizeThemeCustom();
                    setCustom(v);
                } catch {}
            }
        };
        window.addEventListener('storage', handler);
        return () => window.removeEventListener('storage', handler);
    }, []);

    // Re-apply theme after App.js pre-loads site settings from server
    useEffect(() => {
        const onLoaded = () => {
            try {
                const saved = localStorage.getItem(THEME_STORAGE_KEY);
                const v = saved ? normalizeThemeCustom(JSON.parse(saved)) : normalizeThemeCustom();
                // Same theme → keep the same object: this event fires on every
                // boot, and a new object re-rendered the whole app + re-injected CSS.
                setCustom(prev => (JSON.stringify(prev) === JSON.stringify(v) ? prev : v));
            } catch {
                setCustom(normalizeThemeCustom());
            }
        };
        window.addEventListener('hm-settings-loaded', onLoaded);
        return () => window.removeEventListener('hm-settings-loaded', onLoaded);
    }, []);

    // Memoised — see ThemeContext.
    const themeCustomValue = useMemo(() => ({
        custom,
        theme: custom,
        saveCustom,
        updateCustom,
        resetCustom,
    }), [custom, saveCustom, updateCustom, resetCustom]);

    return (
        <ThemeCustomContext.Provider value={themeCustomValue}>
            {children}
        </ThemeCustomContext.Provider>
    );
};
